/**
 * Pricing Configuration System Types
 * Three-tier service catalog, pricing policies, rates, and resolution
 */

import type { Database } from '@planmax/shared-types'

// --------------------------------------------------------------------------
// Database row types
// --------------------------------------------------------------------------

export type ServiceCatalogRow = Database['public']['Tables']['service_catalog']['Row']
export type ServiceCatalogInsert = Database['public']['Tables']['service_catalog']['Insert']
export type ServiceCatalogOverrideRow = Database['public']['Tables']['service_catalog_overrides']['Row']
export type ServiceCatalogGroupRow = Database['public']['Tables']['service_catalog_groups']['Row']
export type PricingCategoryRow = Database['public']['Tables']['pricing_categories']['Row']
export type PricingPolicyRow = Database['public']['Tables']['pricing_policies']['Row']
export type PolicyVersionRow = Database['public']['Tables']['pricing_policy_versions']['Row']
export type PricingRateRow = Database['public']['Tables']['pricing_rates']['Row']
export type LocationPricingAssignmentRow = Database['public']['Tables']['location_pricing_assignments']['Row']
export type LocationCatalogItemRow = Database['public']['Tables']['location_catalog_items']['Row']

// --------------------------------------------------------------------------
// Enum types
// --------------------------------------------------------------------------

export type ServiceType = 'fuel' | 'ramp_fee' | 'parking' | 'hangar_nightly' | 'service' | 'oil' | 'concession'

export type UnitOfMeasure =
  | 'gallon' | 'quart' | 'per_night' | 'per_day' | 'per_service'
  | 'per_event' | 'per_occurrence' | 'per_bin' | 'per_load'
  | 'per_hour' | 'percentage' | 'each'

export type FuelType = 'jet_a' | 'avgas_100ll' | 'avgas_ul94' | 'saf'

export type PolicyScope = 'org' | 'location'

export type PolicyStatus = 'draft' | 'active' | 'archived'

export type VersionStatus = 'draft' | 'active' | 'superseded'

export type CustomerType =
  | 'credit_card' | 'annual_lease' | 'contract_fuel'
  | 'tenant_other' | 'self_service' | 'full_service'

export type PricingSource = 'policy' | 'contract' | 'manual' | 'override' | 'dynamic'

export type ContractPriceType = 'fixed' | 'discount_amount' | 'discount_percent' | 'cost_plus' | 'index_plus'

// --------------------------------------------------------------------------
// Frontend domain types
// --------------------------------------------------------------------------

export interface ServiceCatalogItem {
  id: string
  organizationId: string | null
  code: string
  name: string
  description: string | null
  type: ServiceType
  unitOfMeasure: UnitOfMeasure
  fuelType: FuelType | null
  defaultGlCode: string | null
  defaultRevenueCategory: string | null
  isThirdParty: boolean
  isInventoryItem: boolean
  isSystemItem: boolean
  sortOrder: number
  isActive: boolean
  groupId: string | null
}

/** Resolved catalog item from the three-tier merge (RPC result) */
export interface ResolvedCatalogItem {
  catalogItemId: string
  code: string
  displayName: string
  description: string | null
  type: ServiceType
  unitOfMeasure: UnitOfMeasure
  fuelType: FuelType | null
  glCode: string | null
  revenueCategory: string | null
  isSystemItem: boolean
  isThirdParty: boolean
  isInventoryItem: boolean
  sortOrder: number
  groupId: string | null
  groupCode: string | null
  groupName: string | null
  groupSortOrder: number
}

export interface ServiceCatalogGroup {
  id: string
  organizationId: string | null
  code: string
  name: string
  description: string | null
  allowedTypes: string[] | null
  icon: string | null
  sortOrder: number
  isSystemGroup: boolean
  isActive: boolean
}

export interface CatalogOverride {
  id: string
  organizationId: string
  catalogItemId: string
  displayName: string | null
  description: string | null
  glCode: string | null
  revenueCategory: string | null
  sortOrder: number | null
  isActive: boolean
}

export interface PricingCategory {
  id: string
  organizationId: string
  code: string
  name: string
  description: string | null
  sortOrder: number
  matchBaseCategory: string | null
  matchEngineCount: number | null
  matchMinWeight: number | null
  matchMaxWeight: number | null
  matchFuelType: string | null
  icon: string | null
  isActive: boolean
}

export interface PricingPolicy {
  id: string
  organizationId: string
  name: string
  description: string | null
  scope: PolicyScope
  ownerLocationId: string | null
  isTaxInclusive: boolean
  forkedFromId: string | null
  forkedFromVersionId: string | null
  forkedAt: string | null
  status: PolicyStatus
}

export interface PolicyVersion {
  id: string
  policyId: string
  versionNumber: number
  effectiveFrom: string
  effectiveTo: string | null
  label: string | null
  notes: string | null
  status: VersionStatus
}

export interface PricingRate {
  id: string
  versionId: string
  catalogItemId: string
  pricingCategoryId: string | null
  customerType: CustomerType | null
  volumeMinGallons: number | null
  volumeMaxGallons: number | null
  unitId: string | null
  price: number
  costPerUnit: number | null
  waiverMinFuelGallons: number | null
  includesTowning: boolean | null
  contractFuelProvider: string | null
  contractFuelNotes: string | null
  weeklyRate: number | null
  monthlyRate: number | null
  notes: string | null
  sortOrder: number
}

export interface LocationPricingAssignment {
  id: string
  locationId: string
  policyId: string
  priority: number
  activeDays: number[] | null
  effectiveFrom: string | null
  effectiveTo: string | null
  isActive: boolean
  notes: string | null
}

export interface PricingResolutionResult {
  unit_price: number | null
  unit_of_measure: string
  source: PricingSource
  source_detail: {
    policy_name?: string
    priority?: number
    version_id?: string
    price_type?: string
    index_reference?: string
    message?: string
  }
  pricing_category_id: string | null
  cost_per_unit?: number | null
  waiver_min_fuel_gallons?: number | null
  includes_towing?: boolean | null
}

// --------------------------------------------------------------------------
// Mapper functions
// --------------------------------------------------------------------------

export function mapServiceCatalogRow(row: ServiceCatalogRow): ServiceCatalogItem {
  return {
    id: row.id,
    organizationId: row.organization_id,
    code: row.code,
    name: row.name,
    description: row.description,
    type: row.type as ServiceType,
    unitOfMeasure: row.unit_of_measure as UnitOfMeasure,
    fuelType: row.fuel_type as FuelType | null,
    defaultGlCode: row.default_gl_code,
    defaultRevenueCategory: row.default_revenue_category,
    isThirdParty: row.is_third_party,
    isInventoryItem: row.is_inventory_item,
    isSystemItem: row.is_system_item,
    sortOrder: row.sort_order,
    isActive: row.is_active,
    groupId: row.group_id,
  }
}

export function mapCatalogGroupRow(row: ServiceCatalogGroupRow): ServiceCatalogGroup {
  return {
    id: row.id,
    organizationId: row.organization_id,
    code: row.code,
    name: row.name,
    description: row.description,
    allowedTypes: row.allowed_types,
    icon: row.icon,
    sortOrder: row.sort_order,
    isSystemGroup: row.is_system_group,
    isActive: row.is_active,
  }
}

export function mapCatalogOverrideRow(row: ServiceCatalogOverrideRow): CatalogOverride {
  return {
    id: row.id,
    organizationId: row.organization_id,
    catalogItemId: row.catalog_item_id,
    displayName: row.display_name,
    description: row.description,
    glCode: row.gl_code,
    revenueCategory: row.revenue_category,
    sortOrder: row.sort_order,
    isActive: row.is_active,
  }
}

export function mapPricingCategoryRow(row: PricingCategoryRow): PricingCategory {
  return {
    id: row.id,
    organizationId: row.organization_id,
    code: row.code,
    name: row.name,
    description: row.description,
    sortOrder: row.sort_order,
    matchBaseCategory: row.match_base_category,
    matchEngineCount: row.match_engine_count,
    matchMinWeight: row.match_min_weight,
    matchMaxWeight: row.match_max_weight,
    matchFuelType: row.match_fuel_type,
    icon: row.icon,
    isActive: row.is_active,
  }
}

export function mapPricingPolicyRow(row: PricingPolicyRow): PricingPolicy {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    description: row.description,
    scope: row.scope as PolicyScope,
    ownerLocationId: row.owner_location_id,
    isTaxInclusive: row.is_tax_inclusive,
    forkedFromId: row.forked_from_id,
    forkedFromVersionId: row.forked_from_version_id,
    forkedAt: row.forked_at,
    status: row.status as PolicyStatus,
  }
}

export function mapPolicyVersionRow(row: PolicyVersionRow): PolicyVersion {
  return {
    id: row.id,
    policyId: row.policy_id,
    versionNumber: row.version_number,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to,
    label: row.label,
    notes: row.notes,
    status: row.status as VersionStatus,
  }
}

export function mapPricingRateRow(row: PricingRateRow): PricingRate {
  return {
    id: row.id,
    versionId: row.version_id,
    catalogItemId: row.catalog_item_id,
    pricingCategoryId: row.pricing_category_id,
    customerType: row.customer_type as CustomerType | null,
    volumeMinGallons: row.volume_min_gallons,
    volumeMaxGallons: row.volume_max_gallons,
    unitId: row.unit_id,
    price: Number(row.price),
    costPerUnit: row.cost_per_unit != null ? Number(row.cost_per_unit) : null,
    waiverMinFuelGallons: row.waiver_min_fuel_gallons,
    includesTowning: row.includes_towing,
    contractFuelProvider: row.contract_fuel_provider,
    contractFuelNotes: row.contract_fuel_notes,
    weeklyRate: row.weekly_rate != null ? Number(row.weekly_rate) : null,
    monthlyRate: row.monthly_rate != null ? Number(row.monthly_rate) : null,
    notes: row.notes,
    sortOrder: row.sort_order,
  }
}

export function mapAssignmentRow(row: LocationPricingAssignmentRow): LocationPricingAssignment {
  return {
    id: row.id,
    locationId: row.location_id,
    policyId: row.policy_id,
    priority: row.priority,
    activeDays: row.active_days,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to,
    isActive: row.is_active,
    notes: row.notes,
  }
}

// --------------------------------------------------------------------------
// UI label records
// --------------------------------------------------------------------------

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  fuel: 'Fuel',
  ramp_fee: 'Ramp Fee',
  parking: 'Parking',
  hangar_nightly: 'Hangar Nightly',
  service: 'Service',
  oil: 'Oil',
  concession: 'Concession',
}

export const UNIT_OF_MEASURE_LABELS: Record<UnitOfMeasure, string> = {
  gallon: 'Gallon',
  quart: 'Quart',
  per_night: 'Per Night',
  per_day: 'Per Day',
  per_service: 'Per Service',
  per_event: 'Per Event',
  per_occurrence: 'Per Occurrence',
  per_bin: 'Per Bin',
  per_load: 'Per Load',
  per_hour: 'Per Hour',
  percentage: 'Percentage',
  each: 'Each',
}

export const PRICING_SOURCE_LABELS: Record<PricingSource, string> = {
  policy: 'Published Rate',
  contract: 'Contract Rate',
  manual: 'Manual Entry',
  override: 'Override',
  dynamic: 'Dynamic',
}

export const POLICY_STATUS_LABELS: Record<PolicyStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
}

export const VERSION_STATUS_LABELS: Record<VersionStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  superseded: 'Superseded',
}

export const GROUP_ICON_MAP: Record<string, string> = {
  fuel: 'Fuel',
  parking: 'ParkingCircle',
  warehouse: 'Warehouse',
  wrench: 'Wrench',
  droplet: 'Droplet',
  store: 'Store',
}
